/**
 * AdminOverviewTab - KPI cards + funnel bar chart + daily trend + channel summary table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { safeLocaleString } from '@/lib/utils';
import {
  getDashboardOverview,
  getDashboardChannels,
  getDashboardDaily,
  getDashboardAvgTime,
  type OverviewStats,
  type ChannelRow,
  type DailyEventCount,
  type AvgTime,
} from '@/services/adminDashboardService';

const PERIODS = [
  { value: '1d', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

const AdminOverviewTab: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [channelFilter, setChannelFilter] = useState('');
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [daily, setDaily] = useState<DailyEventCount[]>([]);
  const [avgTimeCarousel, setAvgTimeCarousel] = useState<AvgTime | null>(null);
  const [avgTimePayment, setAvgTimePayment] = useState<AvgTime | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, channelsData, dailyData, avgCarousel, avgPayment] = await Promise.all([
        getDashboardOverview(period),
        getDashboardChannels(period),
        getDashboardDaily(period),
        getDashboardAvgTime(period, 'account_created', 'carousel_created'),
        getDashboardAvgTime(period, 'account_created', 'payment_approved'),
      ]);
      setStats(overviewData);
      setChannels(channelsData.channels);
      setDaily(dailyData.data || []);
      setAvgTimeCarousel(avgCarousel);
      setAvgTimePayment(avgPayment);
    } catch (err) {
      console.error('Failed to fetch overview data', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading || !stats) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  // When a channel is selected, use its row data for KPIs
  const filteredChannel = channelFilter ? channels.find((c) => c.channel === channelFilter) : null;
  const displayStats = filteredChannel
    ? {
        accountsCreated: filteredChannel.accounts,
        carouselsCreated: filteredChannel.carousels,
        checkoutsInitiated: filteredChannel.checkouts,
        paymentsApproved: filteredChannel.payments,
        revenue: filteredChannel.revenue,
        ticketMedio: filteredChannel.payments > 0 ? Math.round((filteredChannel.revenue / filteredChannel.payments) * 100) / 100 : 0,
        conversionRate: filteredChannel.accounts > 0 ? Math.round((filteredChannel.payments / filteredChannel.accounts) * 10000) / 100 : 0,
      }
    : stats;

  const funnelSteps = [
    { label: 'Contas criadas', value: channelFilter ? (filteredChannel?.accounts || 0) : stats.funnel.account_created },
    { label: 'Onboarding iniciado', value: stats.funnel.onboarding_started },
    { label: 'Onboarding completo', value: stats.funnel.onboarding_completed },
    { label: 'Carrossel criado', value: channelFilter ? (filteredChannel?.carousels || 0) : stats.funnel.carousel_created },
    { label: 'Checkout iniciado', value: channelFilter ? (filteredChannel?.checkouts || 0) : stats.funnel.checkout_initiated },
    { label: 'Pagamento aprovado', value: channelFilter ? (filteredChannel?.payments || 0) : stats.funnel.payment_approved },
  ];

  const maxFunnel = Math.max(...funnelSteps.map((s) => s.value), 1);

  // Build daily chart data (account_created per day)
  const dailyAccounts = daily.filter((d) => d.event_name === 'account_created');
  const maxDaily = Math.max(...dailyAccounts.map((d) => d.count), 1);

  const formatHours = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
  };

  return (
    <div className="space-y-6">
      {/* Period selector + channel filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
        >
          <option value="">Todos os canais</option>
          {channels.map((ch) => (
            <option key={ch.channel} value={ch.channel}>{ch.channel}</option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <KpiCard label="Contas criadas" value={displayStats.accountsCreated} />
        <KpiCard label="Carrosséis" value={displayStats.carouselsCreated} />
        <KpiCard label="Checkouts" value={displayStats.checkoutsInitiated} />
        <KpiCard label="Pagamentos" value={displayStats.paymentsApproved} />
        <KpiCard label="Receita" value={`R$ ${safeLocaleString(displayStats.revenue)}`} />
        <KpiCard label="Ticket Médio" value={`R$ ${safeLocaleString(displayStats.ticketMedio)}`} />
        <KpiCard label="Conversão total" value={`${displayStats.conversionRate}%`} />
        <KpiCard
          label="Tempo até carrossel"
          value={avgTimeCarousel && avgTimeCarousel.sample_size > 0 ? formatHours(avgTimeCarousel.median_hours) : '—'}
        />
      </div>

      {/* Timing metrics */}
      {avgTimePayment && avgTimePayment.sample_size > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Mediana até carrossel</div>
            <div className="text-lg font-bold text-foreground mt-0.5">
              {avgTimeCarousel ? formatHours(avgTimeCarousel.median_hours) : '—'}
            </div>
            <div className="text-xs text-muted-foreground">({avgTimeCarousel?.sample_size || 0} amostras)</div>
          </div>
          <div className="border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Mediana até pagamento</div>
            <div className="text-lg font-bold text-foreground mt-0.5">
              {formatHours(avgTimePayment.median_hours)}
            </div>
            <div className="text-xs text-muted-foreground">({avgTimePayment.sample_size} amostras)</div>
          </div>
        </div>
      )}

      {/* Daily Trend Chart (CSS bars) */}
      {dailyAccounts.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Contas Criadas por Dia</h3>
          <div className="flex items-end gap-1 h-32">
            {dailyAccounts.map((d) => {
              const pct = maxDaily > 0 ? (d.count / maxDaily) * 100 : 0;
              const date = new Date(d.day);
              const label = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${label}: ${d.count}`}>
                  <div className="text-[10px] text-muted-foreground">{d.count}</div>
                  <div className="w-full bg-muted rounded-t" style={{ height: `${Math.max(pct, 3)}%` }}>
                    <div className="w-full h-full bg-primary/70 rounded-t" />
                  </div>
                  <div className="text-[9px] text-muted-foreground truncate w-full text-center">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Funnel Bar Chart (CSS) */}
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Funil Principal</h3>
        <div className="space-y-3">
          {funnelSteps.map((step, i) => {
            const pct = maxFunnel > 0 ? (step.value / maxFunnel) * 100 : 0;
            const prevValue = i > 0 ? funnelSteps[i - 1].value : step.value;
            const dropoff = prevValue > 0 ? ((prevValue - step.value) / prevValue * 100).toFixed(1) : '0';
            return (
              <div key={step.label} className="flex items-center gap-3">
                <div className="w-40 text-xs text-muted-foreground truncate shrink-0">{step.label}</div>
                <div className="flex-1 bg-muted rounded-full h-6 relative overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                </div>
                <div className="w-12 text-right text-sm font-medium text-foreground shrink-0">{step.value}</div>
                {i > 0 && (
                  <div className="w-16 text-right text-xs text-muted-foreground shrink-0">
                    -{dropoff}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Channels Table (all channels, no limit) */}
      {channels.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <h3 className="text-sm font-semibold text-foreground p-4 border-b border-border">
            Canais de Aquisição
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium text-muted-foreground">Canal</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Contas</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Carrosséis</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Checkouts</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Pagamentos</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Conversão</th>
                  <th className="text-right px-4 py-2 font-medium text-muted-foreground">Receita</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((ch) => (
                  <tr key={ch.channel} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium text-foreground">{ch.channel}</td>
                    <td className="text-right px-4 py-2 text-foreground">{ch.accounts}</td>
                    <td className="text-right px-4 py-2 text-foreground">{ch.carousels}</td>
                    <td className="text-right px-4 py-2 text-foreground">{ch.checkouts}</td>
                    <td className="text-right px-4 py-2 text-foreground">{ch.payments}</td>
                    <td className="text-right px-4 py-2 text-foreground">
                      {ch.accounts > 0 ? ((ch.payments / ch.accounts) * 100).toFixed(1) : '0'}%
                    </td>
                    <td className="text-right px-4 py-2 text-foreground">
                      R$ {safeLocaleString(ch.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold text-foreground mt-1">{value}</div>
    </div>
  );
}

export default AdminOverviewTab;
